import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { CreateUpdateGeofenceParams, Geofence, GeofenceSetting } from '@app-asset-config/models/geofence.model';
import { GeofenceService } from '@app-asset-config/services/geofence.service';
import { dirtyCheck } from '@app-core/models/dirty-check';
import { ConfirmDialogComponent } from '@app-shared/components/dialog/confirm-dialog/confirm-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { of, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { GeofenceHomeComponent } from '../geofence-home/geofence-home.component';
import { GeofenceSettingsListingComponent } from '../geofence-settings-listing/geofence-settings-listing.component';
import { DataService } from '@app-core/services/data/data.service';

@Component({
  selector: 'app-geofence-create',
  templateUrl: './geofence-create.component.html',
  styleUrls: ['./geofence-create.component.scss'],
})
export class GeofenceCreateComponent implements OnInit, OnDestroy {
  @Input() geofence: Geofence;

  public editMode = false;

  public selectedSetting: GeofenceSetting;

  public unSubscribe = new Subject<void>();

  public geofenceForm = new FormGroup({
    geofenceName: new FormControl('', [
      Validators.required,
      Validators.minLength(3),
      Validators.maxLength(128),
      (control: FormControl) => {
        const value = control.value || '';
        return value.trim().length >= 3 ? null : { minlength: { requiredLength: 3, actualLength: value.trim().length } };
      },
    ]),
    polygonCoordinates: new FormArray([], Validators.minLength(3)),
    propertyId: new FormControl(null, Validators.required),
    tagIds: new FormControl([]),
    address: new FormControl('', Validators.maxLength(512)),
  });

  public isDirty$ = of(false);

  // tag filter related variable
  public unSelectAllTag = false;
  public preSelectedTag: any[] = [];

  constructor(
    private geofenceService: GeofenceService,
    private dialog: MatDialog,
    private translate: TranslateService,
    public dataService: DataService
  ) {}

  ngOnInit(): void {
    this.selectedSetting = this.geofenceService.geofenceDefaultSettingSubject.value;

    // edit mode
    if (this.geofence) {
      this.editMode = true;
      this.preSelectedTag = this.geofence.tagIds?.map((id) => {
        return { tagId: id };
      });

      this.selectedSetting = {
        propertyName: this.geofence.propertyName,
        colourHex: this.geofence.colourHex,
        propertyId: this.geofence.propertyId,
      } as GeofenceSetting;

      this.geofenceForm.patchValue({ ...this.geofence, address: this.geofence.address?.completeAddress });

      this.geofenceForm.setControl(
        'polygonCoordinates',
        new FormArray(
          this.geofence.polygonCoordinates.map(
            (coord) =>
              new FormGroup({
                longitude: new FormControl(coord.longitude),
                latitude: new FormControl(coord.latitude),
              })
          ),
          [Validators.minLength(1)]
        )
      );

      // to put the geofence map in update mode
      this.geofenceService.commonDataBus.next({ event: 'SHOW_UPDATE_GEOFENCE' });

      // pass the geofence(coordinate) to map component to draw fence
      this.geofenceService.commonDataBus.next({ event: 'DRAW_GEOFENCE', data: this.geofence });
    }

    if (!this.editMode) {
      this.geofenceService.commonDataBus.next({ event: 'SHOW_CREATE_GEOFENCE_SEARCH' });

      // set the default value for propertyId
      this.geofenceForm.patchValue({ propertyId: this.selectedSetting.propertyId });

      this.geofenceService.commonDataBus.next({ event: 'SET_GEOFENCE_COLOR', data: { colourHex: this.selectedSetting.colourHex } });
    }

    // For geofence coordinates, even tiny lat/lng changes must mark dirty.
    // Use strict numeric comparison for this form instead of the global epsilon (0.1).
    this.isDirty$ = this.geofenceForm.valueChanges.pipe(takeUntil(this.unSubscribe), dirtyCheck(of(this.geofenceForm.value), 0));

    // subscribing to geofence coordinates
    this.geofenceService.commonDataBus.pipe(takeUntil(this.unSubscribe)).subscribe(({ event, data }) => {
      if (event === 'GEOFENCE_COORDINATES') {
        this.geofenceForm.setControl(
          'polygonCoordinates',
          new FormArray(
            data.map(
              (coord) =>
                new FormGroup({
                  longitude: new FormControl(coord[0]),
                  latitude: new FormControl(coord[1]),
                })
            ),
            [Validators.minLength(1)]
          )
        );

        this.geofenceForm.updateValueAndValidity();
      }

      if (event === 'DISABLE_GEOFENCE') {
        this.disableGeofence();
      }

      if (event === 'ENABLE_GEOFENCE') {
        this.enableGeofence();
      }

      if (event === 'DELETE_GEOFENCE') {
        this.deleteGeofence();
      }
    });
  }

  ngOnDestroy(): void {
    this.unSubscribe.next();
  }

  goBack(option = {}) {
    // setting contents on map
    this.geofenceService.commonDataBus.next({ event: 'SHOW_HOME' });
    this.geofenceService.pop(option);
  }

  navigateToSettingSelection() {
    const data = {
      'output:selected': this.onSelectSetting.bind(this),
      ...{ preSelect: this.selectedSetting },
      mode: 'select',
      from: 'filter',
    };

    this.geofenceService.navigateTo(GeofenceSettingsListingComponent, data);
  }

  createGeofence() {
    if (this.geofenceForm.invalid) {
      return;
    }

    // trimming geofence name
    this.geofenceForm.get('geofenceName').setValue(this.geofenceForm.value.geofenceName.trim(), { emitEvent: false });

    // edit mode
    if (this.editMode) {
      return this.updateGeofence();
    }

    // check polygonCoordinates exists in geofenceForm atleast 4 point should be there is not throw error
    const polygonCoordinates = this.geofenceForm.get('polygonCoordinates') as FormArray;
    if (polygonCoordinates.length < 4) {
      this.geofenceService.showToasts('Search a location to begin drawing a geofence, need a gefence to save', 'error');

      this.geofenceService.commonDataBus.next({ event: 'SHOW_CREATE_GEOFENCE_SEARCH' });
      return;
    }

    const geofence = new CreateUpdateGeofenceParams(this.geofenceForm.value as any);

    const body = {
      propertyId: this.geofenceForm.value.propertyId,
      geofences: [{ ...geofence }],
    };

    if (this.geofenceForm.value.address) {
      body.geofences[0]['address'] = {
        completeAddress: this.geofenceForm.value.address,
      };
    }

    this.geofenceService.createGeofence(body).subscribe(
      (res: any) => {
        // when no errors
        if (!res.errors.length) {
          this.geofenceService.fetchGeofence(true);
          this.goBack({ clearEmbedded: true });

          this.geofenceService.showToasts(this.translate.instant('geofenceToastGeofenceCreated'));

          return;
        }

        this.geofenceService.showToasts(this.translate.instant('geofenceToastGeofenceCreationError'), 'error');
      },
      (err) => {
        console.log('Error occurred... : ', err);
        this.geofenceService.showToasts(this.translate.instant('geofenceToastGeofenceCreationError'), 'error');
      }
    );
  }

  updateGeofence() {
    const body = new CreateUpdateGeofenceParams(this.geofenceForm.value as any);

    this.geofenceService.updateGeofence(this.geofence.geofenceId, body).subscribe(
      () => {
        this.geofenceService.showToasts(this.translate.instant('geofenceToastGeofenceUpdated'));
        this.geofenceService.fetchGeofence(true);
        this.goBack();
      },
      (err) => {
        this.geofenceService.showToasts(this.translate.instant('geofenceToastGeofenceUpdationError'), 'error');
        console.log('Error occurred... : ', err);
      }
    );
  }

  onSelectTag(tagIds) {
    this.geofenceForm.patchValue({ tagIds });
  }

  onSelectSetting(setting: GeofenceSetting) {
    this.geofenceForm.patchValue({ propertyId: setting.propertyId });

    // updating this to show in UI
    this.selectedSetting = { ...setting };

    this.geofenceService.commonDataBus.next({ event: 'SET_GEOFENCE_COLOR', data: { colourHex: setting.colourHex } });
  }

  // function to disable geofence
  disableGeofence() {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '350px',
      autoFocus: false,
      disableClose: true,
      panelClass: ['mobile-modal'],
      data: {
        title: this.translate.instant('geofenceModalDisableGeofenceHeading'),
        content: this.translate.instant('geofenceModalDisableGeofenceContent'),
        actions: [
          { actionButton: this.translate.instant('geofenceModalDisableActionCancel'), dialogCloseValue: false },
          { actionButton: this.translate.instant('geofenceModalDisableActionDisable'), dialogCloseValue: true, primary: true },
        ],
        showCloseIcon: true,
      },
    });

    dialogRef.afterClosed().subscribe((value: any) => {
      if (value) {
        this.geofenceService.updateGeofence(this.geofence.geofenceId, { status: 'INACTIVE' }).subscribe(
          () => {
            this.geofence.status = 'INACTIVE';

            this.geofenceService.commonDataBus.next({ event: 'DRAW_GEOFENCE', data: { ...this.geofence } });

            this.geofenceService.fetchGeofence(true);

            this.geofenceService.showToasts('Disabled geofence!');
          },
          (err) => {
            this.geofenceService.showToasts('Error disabling geofence', 'error');
            console.log('Error happend in disabling geofence : ', err);
          }
        );
      }
    });
  }

  // function to enable geofence
  enableGeofence() {
    this.geofenceService.updateGeofence(this.geofence.geofenceId, { status: 'ACTIVE' }).subscribe(
      () => {
        this.geofence.status = 'ACTIVE';

        this.geofenceService.fetchGeofence(true);

        this.geofenceService.commonDataBus.next({ event: 'DRAW_GEOFENCE', data: { ...this.geofence } });

        this.geofenceService.showToasts('Enabled geofence!');
      },
      (err) => {
        this.geofenceService.showToasts('Error enabling geofence', 'error');
        console.log('Error happend in enabling geofence : ', err);
      }
    );
  }

  // function to delete geofence
  deleteGeofence() {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '350px',
      autoFocus: false,
      disableClose: true,
      panelClass: ['mobile-modal'],
      data: {
        title: this.translate.instant('geofenceModalDeleteGeofenceHeading'),
        content: this.translate.instant('geofenceModalDeleteGeofenceContent'),
        actions: [
          { actionButton: this.translate.instant('geofenceModalDeleteActionCancel'), dialogCloseValue: false },
          { actionButton: this.translate.instant('geofenceModalDeleteActionDelete'), dialogCloseValue: true, primary: true },
        ],
        showCloseIcon: true,
      },
    });

    dialogRef.afterClosed().subscribe((value: any) => {
      if (value) {
        this.geofenceService.deleteGeofence(this.geofence.geofenceId).subscribe(
          () => {
            this.geofenceService.pop();
            this.geofenceService.navigateTo(GeofenceHomeComponent);

            this.geofenceService.showToasts('Geofence deleted');
          },
          (err) => {
            this.geofenceService.showToasts('Error deleting geofence', 'error');
            console.log('Error happend in disabling geofence : ', err);
          }
        );
      }
    });
  }
}
